import { AxiosResponse } from 'axios';
import {
  GET_JOBS_PARAMS_REQUEST,
  JOB_DETAIL_TYPE,
  JOB_TRANSACTION_PARAMS_REQUEST,
  JOB_TRANSACTION_STATE,
  JOBS_BODY_RESULT,
  PayloadActionData,
  ResponseType,
} from 'models';
import { call, debounce, put, takeLatest } from 'redux-saga/effects';
import {
  ADJUSTMENT_SURCHARGE_RESPONSE,
  CHECK_CAN_CREATE_TRIAL_JOB_PARAMS,
  CREATE_ADJUSTMENT_SURCHARGE_PAYLOAD,
  GET_ADJUSTMENT_SURCHARGE_PARAMS,
  UPDATE_ADJUSTMENT_SURCHARGE_PAYLOAD,
} from './../../../models/jobs';

import { PayloadAction } from '@reduxjs/toolkit';
import { NotifyService } from 'helpers';
import {
  addAdjustmentSurchargeApi,
  canCreateTrialJob,
  deleteAdjustmentSurchargeApi,
  deleteJobApi,
  getAdjustmentSurchargeApi,
  getJobDetailApi,
  getJobsListApi,
  getJobTransactionsApi,
  updateAdjustmentSurchargeApi,
} from 'services/jobs';
import { setGlobalLoading, setLoadingPopup } from 'store/reducers/global';
import {
  setAdjustmentSurchargeList,
  setIsAllowCreateTrialJob,
  setJobsDetailSuccess,
  setJobsListSuccess,
  setJobTransactionSuccess,
} from 'store/reducers/jobs';
import {
  CHECK_CAN_CREATE_TRIAL_JOB,
  CREATE_ADJUSTMENT_SURCHARGE_REQUEST,
  DELETE_ADJUSTMENT_SURCHARGE_REQUEST,
  DELETE_JOBS_REQUEST,
  GET_ADJUSTMENT_SURCHARGE_REQUEST,
  GET_JOB_TRANSACTIONS_REQUEST,
  GET_JOBS_DETAIL_REQUEST,
  GET_JOBS_LIST_REQUEST,
  GET_JOBS_LIST_REQUEST_WITH_KEYWORD,
  UPDATE_ADJUSTMENT_SURCHARGE_REQUEST,
} from 'store/reducers/jobs/actionTypes';

function* setJobsListRequest(
  action: PayloadAction<{
    params?: GET_JOBS_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    const response: AxiosResponse<JOBS_BODY_RESULT> = yield call(
      getJobsListApi,
      action.payload.params
    );
    yield put(
      setJobsListSuccess({
        records: response.data.records,
        metadata: response.data.metadata,
      })
    );
    yield put(setGlobalLoading(false));
  } catch (error: any) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  }
}

function* deleteJobRequest(
  action: PayloadAction<{
    id: string;
    onSuccess?: () => void;
  }>
) {
  try {
    yield call(deleteJobApi, action.payload.id);
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {}
}

function* getJobDetailRequest(
  action: PayloadAction<PayloadActionData<{ id: string }>>
) {
  if (action.payload.isPopup) yield put(setLoadingPopup(true));
  else yield put(setGlobalLoading(true));
  try {
    const response: AxiosResponse<ResponseType<JOB_DETAIL_TYPE>> = yield call(
      getJobDetailApi,
      action.payload.data.id
    );
    yield put(setJobsDetailSuccess(response.data.data));
    if (action.payload.onSuccess) action.payload.onSuccess();
  } catch (error: unknown) {}
  if (action.payload.isPopup) yield put(setLoadingPopup(false));
  else yield put(setGlobalLoading(false));
}

function* setJobTransactionsRequest(
  action: PayloadAction<{
    params?: JOB_TRANSACTION_PARAMS_REQUEST;
  }>
) {
  try {
    yield put(setGlobalLoading(true));
    const response: AxiosResponse<PayloadActionData<JOB_TRANSACTION_STATE>> =
      yield call(getJobTransactionsApi, action.payload.params);

    yield put(
      setJobTransactionSuccess({
        records: response.data.data.records,
        metadata: response.data.data.metadata,
      })
    );
    yield put(setGlobalLoading(false));
  } catch (error: any) {
    NotifyService.error(error);
    yield put(setGlobalLoading(false));
  }
}

function* checkIsAllowCreateTrialJob(
  action: PayloadAction<{
    params: CHECK_CAN_CREATE_TRIAL_JOB_PARAMS;
  }>
) {
  try {
    const response: AxiosResponse<PayloadActionData<boolean>> = yield call(
      canCreateTrialJob,
      action.payload.params
    );
    yield put(setIsAllowCreateTrialJob(response.data.data));
  } catch (error: any) {
    NotifyService.error(error);
  }
}

function* getAdjustmentSurchargeRequest(
  action: PayloadAction<{
    params: GET_ADJUSTMENT_SURCHARGE_PARAMS;
  }>
) {
  try {
    const response: AxiosResponse<
      PayloadActionData<ADJUSTMENT_SURCHARGE_RESPONSE>
    > = yield call(getAdjustmentSurchargeApi, action.payload.params);
    yield put(setAdjustmentSurchargeList(response.data.data));
  } catch (error: any) {
    NotifyService.error(error);
  }
}

function* createAdjustmentSurchargeRequest(
  action: PayloadAction<PayloadActionData<CREATE_ADJUSTMENT_SURCHARGE_PAYLOAD>>
) {
  try {
    yield call(addAdjustmentSurchargeApi, action.payload.data);
    action.payload.onSuccess?.();
  } catch (error: any) {
    NotifyService.error(error);
    action.payload.onFailed?.();
  }
}

function* updateAdjustmentSurchargeRequest(
  action: PayloadAction<PayloadActionData<UPDATE_ADJUSTMENT_SURCHARGE_PAYLOAD>>
) {
  try {
    yield call(updateAdjustmentSurchargeApi, action.payload.data);
    action.payload.onSuccess?.();
  } catch (error: any) {
    NotifyService.error(error);
    action.payload.onFailed?.();
  }
}
function* deleteAdjustmentSurchargeRequest(
  action: PayloadAction<PayloadActionData<{ adjId: string }>>
) {
  try {
    yield call(deleteAdjustmentSurchargeApi, action.payload.data.adjId);
    action.payload.onSuccess?.();
  } catch (error: any) {
    NotifyService.error(error);
    action.payload.onFailed?.();
  }
}

function* jobs() {
  yield takeLatest(GET_JOBS_LIST_REQUEST, setJobsListRequest);
  yield debounce(250, GET_JOBS_LIST_REQUEST_WITH_KEYWORD, setJobsListRequest);
  yield takeLatest(DELETE_JOBS_REQUEST, deleteJobRequest);
  yield takeLatest(GET_JOBS_DETAIL_REQUEST, getJobDetailRequest);
  yield takeLatest(GET_JOB_TRANSACTIONS_REQUEST, setJobTransactionsRequest);
  yield takeLatest(CHECK_CAN_CREATE_TRIAL_JOB, checkIsAllowCreateTrialJob);
  yield takeLatest(
    GET_ADJUSTMENT_SURCHARGE_REQUEST,
    getAdjustmentSurchargeRequest
  );
  yield takeLatest(
    UPDATE_ADJUSTMENT_SURCHARGE_REQUEST,
    updateAdjustmentSurchargeRequest
  );
  yield takeLatest(
    CREATE_ADJUSTMENT_SURCHARGE_REQUEST,
    createAdjustmentSurchargeRequest
  );
  yield takeLatest(
    DELETE_ADJUSTMENT_SURCHARGE_REQUEST,
    deleteAdjustmentSurchargeRequest
  );
}

export default jobs;
